import isEmpty from 'lodash/isEmpty';
import { FormLabel, FormControl, Select, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import customCheckboxRadioSwitch from 'assets/jss/material-dashboard-pro-react/customCheckboxRadioSwitch';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import { grayColor } from 'assets/jss/material-dashboard-pro-react';
import GridContainer from 'components/Grid/GridContainer.jsx';
import GridItem from 'components/Grid/GridItem.jsx';
import InfoIconTooltip from 'components/InfoIconTooltip/InfoIconTooltip';

const prepareSingle = (value, trackBy) => {
  if (value === undefined) {
    return null;
  }

  // eslint-disable-next-line no-prototype-builtins -- FIXME
  if (value !== null && value.hasOwnProperty(trackBy)) {
    return value[trackBy];
  }

  return value;
};

const prepareMultiple = (value, trackBy) => {
  if (value === undefined) {
    return null;
  }

  return value.map(el => {
    // eslint-disable-next-line no-prototype-builtins -- FIXME
    if (el.hasOwnProperty(trackBy)) {
      return el[trackBy];
    }

    return el;
  });
};

// eslint-disable-next-line @typescript-eslint/max-params -- FIXME
const handleSingle = (options, trackBy, ev, handleChange) => {
  const selectedValue = options.find(el => el[trackBy] === ev.target.value);

  handleChange(ev, selectedValue);
};

// eslint-disable-next-line @typescript-eslint/max-params -- FIXME
const handleMultiple = (options, trackBy, ev, handleChange) => {
  const selectedValues = options.filter(option =>
    ev.target.value.includes(option[trackBy])
  );

  handleChange(ev, selectedValues);
};

const useStyles = makeStyles({
  selectFormControl: extendedFormsStyle.selectFormControl,
  selectMenu: extendedFormsStyle.selectMenu,
  select: extendedFormsStyle.select,
  selectWrapped: {
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
  },
  selectMenuItem: {
    ...extendedFormsStyle.selectMenuItem,
    '&.Mui-disabled': {
      color: grayColor[14],
      opacity: 1,
    },
  },
  selectMenuItemSelectedMultiple:
    extendedFormsStyle.selectMenuItemSelectedMultiple,
  labelHorizontal: customCheckboxRadioSwitch.labelHorizontal,
  disabled: {
    opacity: '0.4',
    cursor: 'not-allowed',
    '& *': {
      cursor: 'not-allowed !important',
    },
  },
});

const SelectInput = ({
  dataCy = '',
  label,
  name,
  id,
  classes: propClasses,
  options,
  value,
  handleChange,
  mapBy = 'value',
  disabled = false,
  trackBy = '@id',
  tooltip,
  size,
  multiple = false,
  customStyle,
  disabledMenuItems,
  noGrid,
  customLabel,
  className,
  customSelectClasses = {},
  customFormControlClasses = {},
  restyledRootMenuItem,
  restyledRenderValue,
}) => {
  const classes = { ...useStyles(), ...propClasses };

  const defaultValue = multiple
    ? prepareMultiple(value, trackBy)
    : prepareSingle(value, trackBy);

  const handleSelectChange = ev =>
    multiple
      ? handleMultiple(options, trackBy, ev, handleChange)
      : handleSingle(options, trackBy, ev, handleChange);

  const renderValue =
    typeof restyledRenderValue === 'function'
      ? selected =>
          restyledRenderValue({
            selected,
            options,
            trackBy,
            mapBy,
            multiple,
            customLabel,
          })
      : undefined;

  if (noGrid) {
    return (
      <FormControl
        fullWidth={true}
        className={
          isEmpty(customFormControlClasses)
            ? classes.selectFormControl
            : customFormControlClasses.customSelectFormControl
        }
      >
        <Select
          data-cy={dataCy}
          className={className}
          multiple={multiple}
          MenuProps={{ className: classes.selectMenu }}
          style={customStyle}
          classes={{
            root: disabled ? classes.disabled : undefined,
            select: classNames(classes.select, classes.selectWrapped),
            ...customSelectClasses,
          }}
          value={defaultValue}
          onChange={handleSelectChange}
          name={name}
          inputProps={{
            name,
            id,
            disabled,
          }}
          {...(renderValue ? { renderValue } : {})}
        >
          {options.map((option, index) => (
            <MenuItem
              key={index}
              className={`${className}__item`}
              classes={{
                root: restyledRootMenuItem || classes.selectMenuItem,
                selected: classes.selectMenuItemSelectedMultiple,
              }}
              value={option[trackBy]}
              disabled={
                (disabledMenuItems &&
                  disabledMenuItems.some(
                    item => item[trackBy] === option[trackBy]
                  )) ||
                false
              }
            >
              {customLabel ? customLabel(option) : option[mapBy]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <GridContainer alignItems="center">
      <GridItem xs={size || 12}>
        <FormLabel
          className={classNames(classes.labelHorizontal, classes.formLabel)}
        >
          {label} {tooltip && <InfoIconTooltip title={tooltip} />}
        </FormLabel>
        <FormControl
          fullWidth={true}
          className={
            isEmpty(customFormControlClasses)
              ? classes.selectFormControl
              : customFormControlClasses.customSelectFormControl
          }
        >
          <Select
            className={className}
            multiple={multiple}
            MenuProps={{ className: classes.selectMenu }}
            style={customStyle}
            classes={{
              root: disabled ? classes.disabled : undefined,
              select: classNames(classes.select, classes.selectWrapped),
              ...customSelectClasses,
            }}
            value={defaultValue}
            onChange={handleSelectChange}
            name={name}
            inputProps={{
              name,
              id,
              disabled,
            }}
            {...(renderValue ? { renderValue } : {})}
          >
            {options?.map((option, index) => (
              <MenuItem
                key={index}
                className={`${className}__item`}
                disabled={
                  (disabledMenuItems &&
                    disabledMenuItems.some(
                      item => item[trackBy] === option[trackBy]
                    )) ||
                  false
                }
                style={{ zIndex: 70000 }}
                classes={{
                  root: restyledRootMenuItem || classes.selectMenuItem,
                  selected: classes.selectMenuItemSelectedMultiple,
                }}
                value={option[trackBy]}
              >
                {customLabel ? customLabel(option) : option[mapBy]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </GridItem>
    </GridContainer>
  );
};

export default SelectInput;
